import React, { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calculator, MessageCircle, ChevronDown, Percent, Wallet } from "lucide-react";
import { MOTORCYCLES } from "../data/motorcycles";
import { formatIDR, buildWaLinkGeneric } from "../lib/whatsapp";

const TENORS = [12, 24, 35, 47];
const RATE = { 12: 0.07, 24: 0.14, 35: 0.20, 47: 0.26 };
const MIN_DP_PCT = 10;
const MAX_DP_PCT = 90;

// Format angka ribuan ID untuk tampilan input nominal
const formatThousand = (n) => new Intl.NumberFormat("id-ID").format(n);
const parseThousand = (s) => Number(String(s).replace(/[^\d]/g, "")) || 0;

export default function CreditSimulator() {
  const defaultMotor = MOTORCYCLES.find((m) => m.slug === "nmax-turbo") || MOTORCYCLES[0];
  const [slug, setSlug] = useState(defaultMotor.slug);
  const [tenor, setTenor] = useState(24);
  const [mode, setMode] = useState("percent"); // "percent" | "nominal"
  const [dpPct, setDpPct] = useState(20);
  const [dpNominal, setDpNominal] = useState(() => Math.round(0.2 * defaultMotor.price_idr));
  const [dpInputStr, setDpInputStr] = useState(() =>
    formatThousand(Math.round(0.2 * defaultMotor.price_idr))
  );

  const motor = MOTORCYCLES.find((m) => m.slug === slug) || defaultMotor;
  const otr = motor.price_idr;
  const minDp = Math.round((MIN_DP_PCT / 100) * otr);
  const maxDp = Math.round((MAX_DP_PCT / 100) * otr);

  // Ketika motor berganti, sinkronkan nominal berdasarkan mode aktif
  useEffect(() => {
    if (mode === "percent") {
      const n = Math.round((dpPct / 100) * otr);
      setDpNominal(n);
      setDpInputStr(formatThousand(n));
    } else {
      // clamp nominal ke rentang motor baru
      const clamped = Math.min(Math.max(dpNominal, minDp), maxDp);
      setDpNominal(clamped);
      setDpInputStr(formatThousand(clamped));
      setDpPct(Math.round((clamped / otr) * 100));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Nilai DP efektif berdasarkan mode
  const dp = mode === "percent" ? Math.round((dpPct / 100) * otr) : dpNominal;
  const dpPctEffective = mode === "percent" ? dpPct : Math.round((dp / otr) * 100);

  const { monthly } = useMemo(() => {
    const pokok = Math.max(otr - dp, 0);
    const rate = RATE[tenor] ?? 0.14;
    const total = Math.round(pokok * (1 + rate));
    const monthly = pokok > 0 ? Math.ceil(total / tenor / 1000) * 1000 : 0;
    return { monthly };
  }, [otr, dp, tenor]);

  const handlePctChange = (val) => {
    setDpPct(val);
    const n = Math.round((val / 100) * otr);
    setDpNominal(n);
    setDpInputStr(formatThousand(n));
  };

  const handleNominalChange = (raw) => {
    const num = parseThousand(raw);
    setDpInputStr(raw === "" ? "" : formatThousand(num));
    setDpNominal(num);
    setDpPct(otr > 0 ? Math.round((num / otr) * 100) : 0);
  };

  const handleNominalBlur = () => {
    const clamped = Math.min(Math.max(dpNominal, minDp), maxDp);
    setDpNominal(clamped);
    setDpInputStr(formatThousand(clamped));
    setDpPct(Math.round((clamped / otr) * 100));
  };

  const waMsg = `Halo, saya tertarik simulasi kredit Yamaha ${motor.name}.\nHarga OTR: ${formatIDR(
    otr
  )}\nDP: ${dpPctEffective}% (${formatIDR(dp)})\nTenor: ${tenor} bulan\nAngsuran: ${formatIDR(
    monthly
  )}\n\nMohon info angsuran final. Terima kasih.`;

  return (
    <section id="kredit" className="relative py-8 md:py-14 bg-slate-50" data-testid="section-credit">
      <div className="y-container">
        <div className="flex items-center gap-2 mb-4 md:mb-5">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#002d62] text-white">
            <Calculator className="w-4 h-4" />
          </span>
          <h2 className="font-display font-extrabold text-lg sm:text-2xl text-slate-900">
            Simulasi Kredit
          </h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-6"
        >
          <div className="grid gap-3 sm:gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Pilih Motor</span>
              <div className="relative">
                <select
                  data-testid="credit-motor-select"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-9 text-sm font-medium text-slate-800 focus:border-[#002d62] focus:outline-none"
                >
                  {MOTORCYCLES.map((m) => (
                    <option key={m.slug} value={m.slug}>{m.name} · {formatIDR(m.price_idr)}</option>
                  ))}
                </select>
                <ChevronDown className="w-4 h-4 text-slate-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </label>

            <label className="block">
              <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Tenor (bulan)</span>
              <div className="grid grid-cols-4 gap-1.5" data-testid="credit-tenor">
                {TENORS.map((t) => (
                  <button
                    key={t}
                    onClick={() => setTenor(t)}
                    data-testid={`credit-tenor-${t}`}
                    className={`py-2 rounded-lg border text-xs font-semibold transition-all ${
                      tenor === t
                        ? "bg-[#002d62] text-white border-[#002d62]"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </label>
          </div>

          {/* DP Mode toggle */}
          <div className="mt-4 flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Uang Muka (DP)
            </span>
            <div className="inline-flex rounded-full bg-slate-100 p-0.5" data-testid="credit-dp-mode">
              <button
                onClick={() => setMode("percent")}
                data-testid="credit-dp-mode-percent"
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition ${
                  mode === "percent" ? "bg-[#002d62] text-white shadow" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <Percent className="w-3 h-3" /> Persentase
              </button>
              <button
                onClick={() => setMode("nominal")}
                data-testid="credit-dp-mode-nominal"
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition ${
                  mode === "nominal" ? "bg-[#002d62] text-white shadow" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <Wallet className="w-3 h-3" /> Nominal
              </button>
            </div>
          </div>

          {mode === "percent" ? (
            <div className="mt-2" data-testid="credit-dp-percent-panel">
              <div className="flex items-center justify-between mb-1.5 text-xs">
                <span className="text-slate-500">DP: <span className="font-semibold text-slate-900">{dpPct}%</span></span>
                <span className="text-slate-500">= <span className="font-semibold text-slate-900">{formatIDR(dp)}</span></span>
              </div>
              <input
                type="range"
                min={MIN_DP_PCT}
                max={MAX_DP_PCT}
                step={5}
                value={dpPct}
                data-testid="credit-dp-slider"
                onChange={(e) => handlePctChange(Number(e.target.value))}
                className="w-full accent-[#e60012]"
              />
              <div className="mt-1 flex justify-between text-[10px] text-slate-400">
                <span>{MIN_DP_PCT}%</span><span>50%</span><span>{MAX_DP_PCT}%</span>
              </div>
            </div>
          ) : (
            <div className="mt-2" data-testid="credit-dp-nominal-panel">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-500">Rp</span>
                <input
                  type="text"
                  inputMode="numeric"
                  data-testid="credit-dp-nominal-input"
                  value={dpInputStr}
                  onChange={(e) => handleNominalChange(e.target.value)}
                  onBlur={handleNominalBlur}
                  placeholder="Masukkan nominal DP"
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-900 focus:border-[#002d62] focus:outline-none"
                />
              </div>
              <div className="mt-1.5 flex items-center justify-between text-[10px] text-slate-500">
                <span>Setara <span className="font-semibold text-slate-800">{dpPctEffective}%</span> dari OTR</span>
                <span>Rentang: {formatIDR(minDp)} – {formatIDR(maxDp)}</span>
              </div>
              {/* Preset nominal cepat */}
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="credit-dp-nominal-presets">
                {[2_000_000, 3_000_000, 5_000_000, 7_500_000, 10_000_000, 15_000_000].map((v) => (
                  <button
                    key={v}
                    onClick={() => handleNominalChange(String(v))}
                    disabled={v < minDp || v > maxDp}
                    data-testid={`credit-dp-preset-${v}`}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border transition ${
                      dpNominal === v
                        ? "bg-[#002d62] text-white border-[#002d62]"
                        : v < minDp || v > maxDp
                          ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    {formatIDR(v).replace("Rp", "Rp ")}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5 rounded-xl bg-gradient-to-br from-[#002d62] to-[#0a1f44] text-white p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-white/60">Angsuran per bulan</p>
              <p className="font-display font-extrabold text-2xl sm:text-3xl" data-testid="credit-monthly">
                {formatIDR(monthly)}
              </p>
              <p className="text-[10px] text-white/60 mt-0.5">
                Estimasi · Tenor {tenor} bln · DP {dpPctEffective}% ({formatIDR(dp)})
              </p>
            </div>
            <a
              href={buildWaLinkGeneric(waMsg)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="credit-wa-cta"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white text-sm font-semibold transition"
            >
              <MessageCircle className="w-4 h-4" /> Ajukan via WA
            </a>
          </div>
          <p className="mt-2 text-[10px] text-slate-500">
            *Estimasi bunga flat rata-rata leasing. Angsuran final via WhatsApp.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
